// MUST be first. The @Type decorators below are applied when this module is evaluated, and
// class-transformer's Type() calls Reflect.getMetadata('design:type', ...) inside that decorator —
// so the polyfill has to be installed before this module's class bodies run.
//
// Importing it in main.tsx is not sufficient. ES modules evaluate all of a module's imports before
// its own body, so the polyfill sitting in main.tsx's body runs AFTER every chunk main.tsx
// statically imports. In the production build that put `Reflect.defineMetadata` later in the entry
// chunk than the modules importing this file, giving
// "Uncaught TypeError: Reflect.getMetadata is not a function at type.decorator.js" on deploy while
// the dev server — which serves modules unbundled and pre-bundles reflect-metadata as an optimised
// dep — happened to load it early enough to work.
//
// Keeping the import here makes the ordering a language guarantee rather than a chunking accident.
import 'reflect-metadata'
import { Type } from 'class-transformer'
import { SalaryType } from '@/common/types/compensation.def.ts'
import dayjs from 'dayjs'
import { DetailedHcFile, HcFile } from '@/common/types/personal-info.def.ts'
import { PayoutRate } from '@/common/types/pm-info.def.ts'
import { EmptyData } from '@/common/types/misc.def.ts'
import { toDisplayNum } from '@/utils/misc.ts'

export class GeneralEmployeeData {
  updatedBy!: string
  updatedAt!: string
}

export interface IEmployeeInfo {
  employeeId: string
  username: string
  archivedEmployeeId?: string | null
  displayName: string
  companyEmail: string
  companyPhone?: string
  workInfo: WorkInfo | null
  personalInfo: PersonalInfo | null
  effectiveEmployment: Employment | null
  employments: Employment[]
  costDistribution: CostDistribution | null
  financeManagedData: FinanceManagedData | null
  educations: Education[]
  qualifications: Qualification[]
  employmentHistories: EmploymentHistory[]
  groupEmploymentHistories: GroupEmploymentHistory[]
  salary: Salary | null
  medicalInsurances: MedicalInsurance[]
  lifeInsurances: LifeInsurance[]
  bankAccounts: BankAccount[]
  bonuses: Bonus[]
  deferredPayments: DeferredPayment[]
  deferredProgram: DeferredProgram | null
  identificationDocuments: IdentificationDocument[]
  visas: Visa[]
  dependentInfos: DependentInfo[]
  emergencyContacts: EmergencyContact[]
  buyingPowers: BuyingPower[]
  warningLetters: WarningLetter[]
  performanceImprovementPlans: PerformanceImprovementPlan[]
  effectivePmStaticInfo: PmStaticInfo | null
  pmStaticInfos: PmStaticInfo[]
  pmInfo: PmInfo | null
  contractEmployments: ContractEmployment[]
  annualPerformances: AnnualPerformance[]
}

export class EmployeeInfo implements IEmployeeInfo {
  employeeId!: string
  username!: string
  archivedEmployeeId?: string | null
  displayName!: string
  companyEmail!: string
  status!: string
  companyPhone?: string
  pmCode?: string
  addChecklist!: boolean
  joinDate?: string
  @Type(() => WorkInfo)
  workInfo!: WorkInfo | null

  @Type(() => PersonalInfo)
  personalInfo!: PersonalInfo | null

  @Type(() => Employment)
  effectiveEmployment!: Employment | null

  @Type(() => Employment)
  employments: Employment[] = []

  @Type(() => CostDistribution)
  costDistribution!: CostDistribution | null

  @Type(() => FinanceManagedData)
  financeManagedData!: FinanceManagedData | null

  @Type(() => Education)
  educations: Education[] = []

  @Type(() => Qualification)
  qualifications: Qualification[] = []

  @Type(() => EmploymentHistory)
  employmentHistories: EmploymentHistory[] = []

  @Type(() => GroupEmploymentHistory)
  groupEmploymentHistories: GroupEmploymentHistory[] = []

  @Type(() => Salary)
  salary!: Salary | null

  @Type(() => MedicalInsurance)
  medicalInsurances: MedicalInsurance[] = []

  @Type(() => LifeInsurance)
  lifeInsurances: LifeInsurance[] = []

  @Type(() => BankAccount)
  bankAccounts: BankAccount[] = []

  @Type(() => Bonus)
  bonuses: Bonus[] = []

  @Type(() => ExceptionalAward)
  exceptionalAwards: ExceptionalAward[] = []

  @Type(() => DeferredPayment)
  deferredPayments: DeferredPayment[] = []

  @Type(() => DeferredProgram)
  deferredProgram!: DeferredProgram | null

  @Type(() => IdentificationDocument)
  identificationDocuments: IdentificationDocument[] = []

  @Type(() => Visa)
  visas: Visa[] = []

  @Type(() => DependentInfo)
  dependentInfos: DependentInfo[] = []

  @Type(() => EmergencyContact)
  emergencyContacts: EmergencyContact[] = []

  @Type(() => BackgroundCheck)
  backgroundChecks: BackgroundCheck[] = []

  @Type(() => BuyingPower)
  buyingPowers: BuyingPower[] = []

  @Type(() => WarningLetter)
  warningLetters: WarningLetter[] = []

  @Type(() => PerformanceImprovementPlan)
  performanceImprovementPlans: PerformanceImprovementPlan[] = []

  @Type(() => PmStaticInfo)
  effectivePmStaticInfo!: PmStaticInfo | null

  @Type(() => PmStaticInfo)
  pmStaticInfos: PmStaticInfo[] = []

  @Type(() => PmAnnualPerformance)
  pmAnnualPerformances: PmAnnualPerformance[] = []

  @Type(() => PmInfo)
  pmInfo!: PmInfo | null

  @Type(() => ContractEmployment)
  contractEmployments: ContractEmployment[] = []

  @Type(() => AnnualPerformance)
  annualPerformances: AnnualPerformance[] = []
}

export class EmployeePublicInfo {
  employeeId!: string
  displayName!: string
  username!: string
  companyEmail!: string
  status!: string
  gender?: string
  birthday!: string
  joinDate!: string
  employmentStatus!: string
  department!: string
  subDepartment!: string
  subGroup!: string
  country!: string
  employmentEntity!: string
  officeLocation!: OfficeLocation
  title!: string
  businessCardTitle!: string
  businessUnit!: string
  position!: string
  employmentType!: string
  employeeType!: string
  pmCode?: string
  reportingPmCode?: string
  reportingManager?: string
  reportingManagerName?: string
  hasCompletedInfo!: boolean
}

export type EmployeeEligibleOption = EmployeePublicInfo & {
  terminationDate?: string
  probationInfo?: ProbationInfo
  workingMonthCount: number
}

export type EmployeeProfileLite = {
  employeeId: string
  displayName: string
  username: string
  companyEmail: string
  companyPhone: string
  department: string
  country: string
}

export type OfficeLocation = {
  city: string
  code: string
}

export class WorkInfo extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  country!: string
  employmentEntity!: string
  businessUnit!: string
  employmentType!: string
  employeeType!: string
  department!: string
  subDepartment!: string
  subGroup!: string
  officeLocation!: OfficeLocation
  title!: string
  localTitle?: string | null
  businessCardTitle!: string
  position!: string
  corporateRank!: string
  reportingPmCode?: string | null
  reportingManager!: string
  additionalReportingManager?: string | null
  compensationManager?: string | null
  updateReasons!: string[]
  updateRemarks?: string | null
  effectiveDate!: string
}

export class PersonalInfo extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  effectiveDate!: string
  legalFirstName!: string
  legalLastName!: string
  legalMiddleName?: string | null
  legalFullName!: string
  localLegalFullName?: string | null
  gender!: string
  dateOfBirth!: string
  maritalStatus!: string
  nationality!: string
  nationality2?: string | null
  residency!: Residency
  phone?: string | null
  email?: string | null
  @Type(() => Address)
  homeAddress!: Address
  @Type(() => Address)
  communicationAddress!: Address
  totalWorkExpBeforeJoin!: number // in years
  totalRelevantWorkExpBeforeJoin!: number // in years
  languageLevelNative?: string | null
  languageLevelProficient?: string | null
  biography?: string | null
  updateRemarks?: string | null
}

export type Residency = {
  country: string
  status: string
  prStartDate?: string
}

export class DependentInfo extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  legalName!: string
  relationship!: string
  gender!: string
  dateOfBirth?: string | null
  idNumber?: string | null
  visaType?: string | null
  visaNumber?: string | null
  remarks?: string | null
  files: DetailedHcFile[] = []
}

export class EmergencyContact extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  legalName!: string
  relationship!: string
  phoneNumber!: string
  email?: string | null
  address!: Address
}

export class Address {
  postalCode?: string | null
  country!: string
  stateOrProvince?: string | null
  city!: string
  street!: string

  fullAddress() {
    return [this.street, this.city, this.stateOrProvince, this.country].filter(x => x).join(', ')
  }

  equal(target?: Address): boolean {
    return this.postalCode == target?.postalCode
      && this.country == target?.country
      && this.stateOrProvince == target?.stateOrProvince
      && this.city == target?.city
      && this.street == target?.street
  }
}

export class TimeDuration {
  years!: number
  months!: number
  days!: number
}

export class TimeDurationUtils {
  static isEmpty(obj: TimeDuration | null | undefined): boolean | undefined {
    if (obj == undefined) return undefined
    return !obj.years && !obj.months && !obj.days
  }

  static toString(obj: TimeDuration | null | undefined): string | undefined {
    if (obj == undefined) return undefined
    let result = `${obj.months} month(s)`
    if (obj.years > 0) {
      result = `${obj.years} year(s) ` + result
    }
    if (obj.days > 0) {
      result = result + ` ${obj.days} day(s)`
    }
    return result
  }
}

export class TerminationRestrictionInfo {
  type!: string
  @Type(() => TimeDuration)
  duration!: TimeDuration
  startDate?: string | null
  endDate?: string | null
  remarks?: string | null
}

export class ContractEmployment extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  @Type(() => TimeDuration)
  duration!: TimeDuration
  startDate!: string
  endDate!: string
  isExtension!: boolean
  remarks?: string | null
}

export class AnnualPerformance extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  year!: number
  performanceRating?: string | null
  remarks?: string | null
}

export class ProbationInfo {
  @Type(() => TimeDuration)
  duration!: TimeDuration
  startDate?: string | null
  endDate?: string | null
  remarks?: string | null
}

export class Employment extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  username!: string
  archivedEmployeeId?: string
  status!: string
  companyEmail!: string
  companyPhone?: string | null
  companyMobilePhone?: string | null
  joinDate!: string
  groupJoinDate!: string
  hiringType!: string

  @Type(() => ProbationInfo)
  probation!: ProbationInfo

  @Type(() => TimeDuration)
  noticePeriodDuration!: TimeDuration
  noticePeriodStartDate?: string | null
  terminationDate?: string | null
  lastWorkingDay?: string | null
  terminationType?: string | null
  terminationReason?: string | null
  voluntaryResignationReason?: string | null
  terminationRemarks?: string | null
  @Type(() => TerminationRestrictionInfo)
  nonCompetition!: TerminationRestrictionInfo
  @Type(() => TerminationRestrictionInfo)
  nonSolicitation!: TerminationRestrictionInfo

  remarks?: string | null
}

export class Education extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  degree!: string
  program!: string
  instituteName!: string
  gradeAverage?: string | null
  startDate!: string
  endDate?: string | null
  passingDate?: string | null
  description!: string
  documents: HcFile[] = []
}

export class Qualification extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  qualificationName!: string
  refNumber!: string
  type!: string
  issuer!: string
  issueDate?: string | null
  expiryDate?: string | null
  remarks?: string | null
  documents: HcFile[] = []
}

export interface EmploymentHistoryComputedFields {
  yearsOfService: number
}

export class EmploymentHistory extends GeneralEmployeeData implements EmploymentHistoryComputedFields {
  id!: number
  employeeId!: string
  employerName!: string
  startDate!: string
  endDate!: string
  yearsOfService!: number
  designation!: string
  description?: string | null
  salaryCurrency?: string | null
  monthlyBaseSalaryAmount?: number | null
  otherSalaryComponents?: string | null
  @Type(() => Referee)
  referees: Referee[] = []
  documents: HcFile[] = []
}

export class GroupEmploymentHistory extends GeneralEmployeeData implements EmploymentHistoryComputedFields {
  id!: number
  employeeId!: string
  title!: string
  department?: string | null
  employmentEntity!: string
  startDate!: string
  endDate!: string
  yearsOfService!: number
  remarks?: string | null
}

export class Referee {
  name!: string
  phoneNumber!: string
  email!: string
}

export class Salary extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  effectiveDate!: string
  currency!: string
  amount!: number
  type!: string
  updateReasons!: string[]
  updateRemarks?: string | null

  annualAmount() {
    if (this.type === SalaryType.Monthly) {
      return `${this.currency} ${this.amount * 12}`
    } else if (this.type === SalaryType.Annual) {
      return `${this.currency} ${this.amount}`
    }
  }
}

export class Benefit extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  effectiveDate!: string
  medicalInsurancePlan?: string | null
  medicalInsuranceCurrency?: string | null
  medicalInsuranceAnnualPremium?: number | null
  medicalInsuranceTypeOfDependentsCovered?: string | null
  medicalInsuranceNumOfDependentsCovered?: number | null
  lifeInsurancePlan?: string
  lifeInsuranceCurrency?: string | null
  lifeInsuranceAnnualPremium?: number | null
  updateRemarks?: string
}

export class MedicalInsurance extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  plan!: string
  currency!: string
  totalAnnualPremium!: number
  dependentsCoverageType!: string
  dependentsCoveredCount!: number
  startDate!: string
  endDate?: string | null
  remarks?: string | null
}

export class LifeInsurance extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  plan!: string
  currency!: string
  totalAnnualPremium!: number
  startDate!: string
  endDate?: string | null
  remarks?: string | null
}

export class BankAccount extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  bankName!: string
  bankCode!: string
  branchCode?: string | null
  branchName?: string | null
  accountNumber!: string
  nameAsPerBankAccount!: string
  purpose!: string
  remarks?: string | null
  documents: HcFile[] = []
}

export type FundDistribution = {
  name: string
  percent: number
}

export class DeferredProgram extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  effectiveDate!: string
  // In USD
  vestingThreshold!: number
  vestingPercent!: number
  @Type(() => TimeDuration)
  vestingPeriod!: TimeDuration
  fundDistributions: FundDistribution[] = []
}

export type ActualPayment = {
  currency: string
  amount: number
  payDate: string
}

export class BonusDeferredAmount {
  amount!: number
  @Type(() => TimeDuration)
  vestingPeriod?: TimeDuration | null
  vestingDate?: string | null
  amountAfterVesting?: number | null
  actualPayment?: ActualPayment | null
}

export class Bonus extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  type!: string
  currency!: string
  totalAmount!: number
  awardCountry!: string
  financialYear!: string
  @Type(() => TimeDuration)
  clawbackPeriodDuration?: TimeDuration | null
  clawbackPeriodStartDate?: string | null
  clawbackPeriodEndDate?: string | null
  nonDeferredAmount!: number
  nonDeferredAmountActualPayments: ActualPayment[] = []
  nonDeferredPostAuditAmount!: number
  nonDeferredPostAuditAmountActualPayments: ActualPayment[] = []
  payoutCarryForward!: number
  @Type(() => BonusDeferredAmount)
  deferredAmounts: BonusDeferredAmount[] = []
  remarks?: string | null

  getClawbackStatus() {
    if (!this.clawbackPeriodStartDate || !this.clawbackPeriodEndDate) return 'Not Applicable'
    const now = dayjs()
    const start = dayjs(this.clawbackPeriodStartDate)
    const end = dayjs(this.clawbackPeriodEndDate)

    if (now.isBefore(start)) return 'Not Started'
    if (now.isAfter(end)) return 'Passed'
    if (now.isBefore(end)) return 'Effective'
  }
}

export class ExceptionalAwardReinvestment {
  @Type(() => TimeDuration)
  duration!: TimeDuration
  startDate!: string
  endDate!: string
  amount!: number
  amountAfterReinvestment?: number
}

export class ExceptionalAward extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  currency!: string
  amount!: number
  amountAfterVesting?: number
  awardCountry!: string
  financialYear!: string
  awardDate!: string
  vestingDate!: string
  @Type(() => TimeDuration)
  vestingPeriod!: TimeDuration
  @Type(() => TimeDuration)
  clawbackPeriodDuration?: TimeDuration | null
  clawbackPeriodStartDate?: string | null
  clawbackPeriodEndDate?: string | null
  @Type(() => ExceptionalAwardReinvestment)
  reinvestments: ExceptionalAwardReinvestment[] = []
  actualPayments: ActualPayment[] = []
  remarks?: string
}

export class DeferredPayment {
  bonusId!: number
  employeeId!: string
  bonusType!: string
  currency!: string
  amount!: number
  awardYear!: string
  awardCountry!: string
  @Type(() => TimeDuration)
  vestingPeriod!: TimeDuration
  vestingDate!: string
}

export class IdentificationDocument extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  country!: string
  documentNumber!: string
  category!: string
  type!: string
  issueDate?: string | null
  expiryDate?: string | null
  documents: HcFile[] = []
}

export class Visa extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  country!: string
  visaNumber!: string
  type!: string
  issueDate!: string
  expiryDate!: string
  documents: HcFile[] = []
}

export type PmEliteProgram = {
  hasDeploymentBonus: boolean
  deploymentBonusCriteria?: string
  deploymentBonusClawbackTerm?: string
  effectiveDate: string
  effectiveDateCriteria?: string
}

export interface PmInfoComputedFields {
}

export class PmStaticInfo extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  pmCode!: string
  pmStartDate!: string
  pmEndDate?: string | null
  initialBuyingPower!: number
  yearsOfPmExperience!: number
  yearsOfApmExperience!: number
  yearsOfProprietaryTradingExperience!: number
  pedigrees: string[] = []
  eliteProgram?: PmEliteProgram
  remarks!: string
}

export class PmAnnualPerformance extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  financialYear!: number
  lossCarryForward?: number | null
  remarks?: string | null
}

export class PmInfo extends GeneralEmployeeData implements PmInfoComputedFields {
  id!: number
  employeeId!: string
  effectiveDate!: string
  payoutRates: PayoutRate[] = []
  mandate!: string
  countriesOfFocus: string[] = []
  specialties: string[] = []
  strategies: string[] = []
  generalistStyles: [] = []
  updateRemarks!: string
}

export class BackgroundCheck extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  status!: string
  vendor?: string | null
  completionDate?: string | null
  initiationDate?: string | null
  remarks?: string | null
}

export type ScdDataTimelineItem = {
  effectiveDate: string
  operator?: string
  fieldChanges: TimelineFieldChange[]
  remarks: string
  tags: string[]
  isHidden: boolean
}

export type BasicTimeline<T = string> = {
  effectiveDate: string
  value: T
  description: string
}

export type TimelineFieldChange = {
  fieldName: string
  from?: string | null
  to?: string | null
}

export class BuyingPower {
  effectiveDate!: string
  pm!: string
  apm?: string | null
  description?: string | null
  /**
   * Always in USD million
   * */
  buyingPowerUsd!: number
}

export class WarningLetter extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  issueDate!: string
  details!: string
  documents: HcFile[] = []
}

export class PerformanceImprovementPlan extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  startDate!: string
  endDate!: string
  details!: string
}

export type EmployeeIntegrityCheckResult = {
  employeeId: string
  errors: EmployeeIntegrityCheckError[]
}

export type EmployeeIntegrityCheckError = {
  sectionName: string
  message: string
}

export type CostDistributionDetail = {
  source: string
  percent: number
  groupName: string
}

export class CostDistribution extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  effectiveDate!: string
  details: CostDistributionDetail[] = []
  updateRemarks?: string | null
}

export class FinanceManagedData extends GeneralEmployeeData {
  id!: number
  employeeId!: string
  sapTeamCode?: string | null
  effectiveDate!: string
  updateRemarks?: string | null
}

export const getActualPaymentDisplayString = (bonusPayment?: ActualPayment | null) => {
  if (!bonusPayment) return EmptyData.string
  const moneyStr = (!bonusPayment.currency || !bonusPayment.amount) ? 'Undecided' : `${bonusPayment.currency} ${toDisplayNum(
    bonusPayment.amount)}`
  const payDateStr = `(Pay Date: ${bonusPayment.payDate || 'Undecided'})`
  return `${moneyStr} ${payDateStr}`
}

export type EmployeeCeremonyResponseItem = {
  employeeId: string
  displayName: string
  title: string
  country: string
  department: string
  status: string
  refDate: string
}

export type EmployeeCeremonyResponse = {
  birthdays: EmployeeCeremonyResponseItem[]
  workAnniversaries: EmployeeCeremonyResponseItem[]
  newHires: EmployeeCeremonyResponseItem[]
}